import { tweened } from 'svelte/motion';
import { OvalItem } from './OvalItem';
import { FallbackTheme, Themes } from '@tools/styles/ColorThemes.svelte';
import { cubicInOut } from 'svelte/easing';
import { ColorUtils } from '@tools/ColorUtils';
import { get } from 'svelte/store';

export interface Layout {
	theme: string;
	invert?: boolean;
	mask?: boolean;
	fade?: boolean;
	ovals: {
		height: number;
		width: number;
		in?: {
			x?: number;
			y?: number;
			scale?: number;
		};
		out?: {
			x?: number;
			y?: number;
			scale?: number;
		};
	}[];
}

export class OvalLayout {
	public destroyed = false;
	public isDestroying = false;

	public theme = tweened(
		{
			background: `${FallbackTheme.secondary}`,
			foreground: `${FallbackTheme.primary}`
		},
		{
			duration: 800,
			easing: cubicInOut,
			interpolate: (a, b) => (t) => {
				return {
					background: ColorUtils.lerp(a.background, b.background, t),
					foreground: ColorUtils.lerp(a.foreground, b.foreground, t)
				};
			}
		}
	);

	public opacity = tweened(1, {
		duration: 400,
		easing: cubicInOut
	});

	public readonly config: Layout;
	private items: OvalItem[] = [];

	constructor(layout: Layout) {
		this.config = layout;

		const theme = Themes.get(layout.theme) ?? FallbackTheme;
		this.theme.set(
			{
				background: `${theme.secondary}`,
				foreground: `${theme.primary}`
			},
			{ duration: 0 }
		);

		let x = 0;
		let y = 0;
		let columnWidth = 0;

		for (const oval of layout.ovals) {
			if (y + oval.height > 1) {
				y = 0;
				x += columnWidth;
				columnWidth = 0;
			}

			const item = new OvalItem({
				size: {
					width: oval.width,
					height: oval.height
				},
				in: oval.in,
				out: oval.out,
				position: {
					x,
					y
				}
			});

			y += oval.height;
			columnWidth = Math.max(columnWidth, oval.width);

			this.items.push(item);
		}
	}

	public render = (
		ctx?: CanvasRenderingContext2D,
		canvas?: {
			width: number;
			height: number;
		}
	) => {
		if (!ctx || !canvas) return;

		let backgroundColor = get(this.theme).background;
		let itemsColor = get(this.theme).foreground;
		if (this.config.invert) {
			[backgroundColor, itemsColor] = [itemsColor, backgroundColor];
		}

		if (!this.config.mask) {
			ctx.beginPath();
			ctx.rect(0, 0, canvas.width, canvas.height);
			ctx.fillStyle = ColorUtils.hexToCss(backgroundColor, get(this.opacity));
			ctx.fill();
			ctx.closePath();
		}

		ctx.beginPath();
		for (const item of this.items) {
			item.render(ctx, canvas);
		}
		ctx.fillStyle = ColorUtils.hexToCss(itemsColor, get(this.opacity));
		ctx.fill();
		ctx.closePath();

		if (this.config.mask) {
			ctx.globalCompositeOperation = 'xor';
			ctx.fillStyle = backgroundColor;
			ctx.fillRect(0, 0, canvas.width, canvas.height);
		}
	};

	public destroy = async () => {
		this.isDestroying = true;

		for (const item of this.items) {
			item.destroy();
		}
		if (this.config.fade) await this.opacity.set(0);

		this.destroyed = true;
	};
}
